import { type Component, createEffect, createMemo, For, Show } from 'solid-js'
import { createStore, produce, reconcile } from 'solid-js/store'
import { twMerge } from 'tailwind-merge'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import type { CustomerStatesEditorPropsType } from '~/types/CustomerStatesEditor'
import type { CustomerStateConfigType } from '~/types/Settings'
import { KANBAN_COLUMN_STYLES, KANBAN_STAGE_COLORS, kanbanColumnStyle } from '~/utils/kanban'
import { slugify } from '~/utils/slug'

const COLOR_LABELS: Record<string, string> = {
  default: 'Neutro',
  primary: 'Primario',
  info: 'Info',
  success: 'Éxito',
  warning: 'Alerta',
  danger: 'Peligro'
}

const nextOrder = (stages: CustomerStateConfigType[]) => stages.reduce((max, s) => Math.max(max, s.order), 0) + 1

const uniqueKey = (base: string, stages: CustomerStateConfigType[]): string => {
  const taken = new Set(stages.map(s => s.key))
  let key = base
  let suffix = 2
  while (taken.has(key)) {
    key = `${base}-${suffix}`
    suffix++
  }
  return key
}

const CustomerStatesEditor: Component<CustomerStatesEditorPropsType> = props => {
  const [draft, setDraft] = createStore<{ stages: CustomerStateConfigType[] }>({ stages: [] })

  let lastEmitted = ''
  createEffect(() => {
    const serialized = JSON.stringify(props.value)
    if (serialized === lastEmitted) return
    lastEmitted = serialized
    setDraft('stages', reconcile(props.value.map(s => ({ ...s }))))
  })

  const emit = (stages: CustomerStateConfigType[]) => {
    lastEmitted = JSON.stringify(stages)
    props.onChange(stages.map(s => ({ ...s })))
  }

  const reorder = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= draft.stages.length) return
    setDraft(
      'stages',
      produce(list => {
        const [moved] = list.splice(index, 1)
        list.splice(target, 0, moved)
        list.forEach((s, i) => {
          s.order = i + 1
        })
      })
    )
    emit(draft.stages)
  }

  const removeStage = (index: number) => {
    setDraft(
      'stages',
      produce(list => {
        list.splice(index, 1)
        list.forEach((s, i) => {
          s.order = i + 1
        })
      })
    )
    emit(draft.stages)
  }

  const addStage = () => {
    const stages = draft.stages
    const key = uniqueKey(slugify(`etapa-${stages.length + 1}`), stages)
    setDraft('stages', stages.length, { key, label: '', color: 'default', order: nextOrder(stages) })
    emit(draft.stages)
  }

  const setStage = (index: number, patch: Partial<CustomerStateConfigType>) => {
    setDraft('stages', index, patch)
    emit(draft.stages)
  }

  const generateKey = (index: number) => {
    const label = draft.stages[index].label.trim()
    const base = slugify(label || 'etapa')
    setDraft(
      'stages',
      index,
      'key',
      uniqueKey(
        base,
        draft.stages.filter((_, i) => i !== index)
      )
    )
    emit(draft.stages)
  }

  const errors = createMemo(() => {
    const problems: string[] = []
    const keys = draft.stages.map(s => s.key)
    for (const s of draft.stages) {
      if (!s.label.trim()) {
        problems.push(`La etapa "${s.key || 'sin clave'}" no tiene nombre.`)
        break
      }
    }
    const dupes = keys.filter((key, i) => key && keys.indexOf(key) !== i)
    if (dupes.length) {
      problems.push(`Clave repetida: "${dupes[0]}". Cada etapa necesita una clave única.`)
    }
    return problems
  })

  return (
    <div class="space-y-4">
      {/* Preview del tablero */}
      <Show when={draft.stages.length > 0}>
        <div class="flex flex-wrap gap-1.5">
          <For each={draft.stages}>
            {(stage, i) => (
              <div class={twMerge('rounded-full px-3 py-1 text-xs font-medium', kanbanColumnStyle(stage.color).cabecera)}>
                <span class={kanbanColumnStyle(stage.color).numero}>{i() + 1}.</span> {stage.label.trim() || <span class="italic opacity-60">(sin nombre)</span>}
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={draft.stages.length === 0}>
        <p class="text-sm text-foreground-subtle">Sin etapas. El tablero no tendrá columnas.</p>
      </Show>

      {/* Lista de etapas */}
      <div class="space-y-3">
        <For each={draft.stages}>
          {(stage, index) => (
            <div class="rounded-xl border border-border bg-surface-muted/40 p-4">
              {/* Header: número + acciones */}
              <div class="mb-3 flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class={twMerge('flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold', kanbanColumnStyle(stage.color).cabecera, kanbanColumnStyle(stage.color).numero)}>
                    {index() + 1}
                  </span>
                  <span class="text-sm font-medium text-foreground">{stage.label.trim() || <span class="italic text-foreground-subtle">(sin nombre)</span>}</span>
                </div>
                <div class="flex items-center gap-1">
                  <Button
                    size="xs"
                    variant="text"
                    color="default"
                    icon="material-symbols-light:keyboard-arrow-up"
                    aria-label="Subir etapa"
                    disabled={index() === 0}
                    onClick={() => reorder(index(), -1)}
                  />
                  <Button
                    size="xs"
                    variant="text"
                    color="default"
                    icon="material-symbols-light:keyboard-arrow-down"
                    aria-label="Bajar etapa"
                    disabled={index() === draft.stages.length - 1}
                    onClick={() => reorder(index(), 1)}
                  />
                  <Button size="xs" variant="text" color="danger" icon="material-symbols-light:delete-outline" aria-label="Eliminar etapa" onClick={() => removeStage(index())} />
                </div>
              </div>

              {/* Inputs: nombre y clave lado a lado */}
              <div class="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input label="Nombre" placeholder="Cotización, Venta…" value={stage.label} onInput={e => setStage(index(), { label: e.currentTarget.value })} />
                <div class="flex items-end gap-1.5">
                  <div class="flex-1">
                    <Input label="Clave" placeholder="cotizacion" value={stage.key} onInput={e => setStage(index(), { key: slugify(e.currentTarget.value) })} class="font-mono text-xs" />
                  </div>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="default"
                    icon="material-symbols-light:autorenew"
                    aria-label="Generar clave desde el nombre"
                    title="Generar clave desde el nombre"
                    onClick={() => generateKey(index())}
                  />
                </div>
              </div>

              {/* Selector de color */}
              <div class="flex flex-wrap gap-1.5">
                <For each={KANBAN_STAGE_COLORS}>
                  {color => (
                    <button
                      type="button"
                      aria-label={`Color ${COLOR_LABELS[color]}`}
                      title={COLOR_LABELS[color]}
                      onClick={() => setStage(index(), { color })}
                      class={twMerge(
                        'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                        stage.color === color
                          ? 'border-foreground/30 bg-surface-muted text-foreground ring-1 ring-foreground/20'
                          : 'border-border bg-surface text-foreground-subtle hover:border-foreground/30 hover:text-foreground'
                      )}
                    >
                      <span class={twMerge('h-3 w-3 rounded-full', KANBAN_COLUMN_STYLES[color].barra)} />
                      {COLOR_LABELS[color]}
                    </button>
                  )}
                </For>
              </div>
            </div>
          )}
        </For>
      </div>

      {/* Errores */}
      <Show when={errors().length > 0}>
        <ul class="space-y-1">
          <For each={errors()}>
            {problem => (
              <li class="flex items-start gap-1.5 text-xs text-danger">
                <Icon icon="material-symbols-light:error-outline" size={14} class="mt-0.5 shrink-0" />
                <span>{problem}</span>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Button size="sm" variant="outlined" color="primary" icon="material-symbols-light:add" onClick={addStage}>
        Añadir etapa
      </Button>
    </div>
  )
}

export default CustomerStatesEditor
